/**
 * Expense API Service
 * 
 * Service for fetching approved expenses and approved credit card expenses from the external Expense API.
 * This service routes requests through the backend (FE -> BE -> Expense API) for security.
 * Falls back to mock data if the API request fails.
 */

import type { Expense } from '@/types/expense';
import type { Company } from '@/types/company';
import { getApiBaseUrl, getDataMode } from './data-service';
import { apiFetch } from './api-error-handler';
import { mockExpenses } from './mock-data';

export interface ExternalExpenseDetail {
  Id?: string;
  User?: {
    Reference?: string;
    EmailAddress?: string;
    FirstName?: string;
    LastName?: string;
    Id?: string;
    Url?: string;
  };
  ExpenseType?: {
    Name?: string;
    Reference?: string;
    Category?: string;
    EntryType?: string;
  };
  Header?: {
    Id?: string;
    Sequence?: number;
    ExpenseDate?: string;
    Reference?: string;
    Description?: string;
    Gross?: number;
  };
  Sequence?: number;
  Category?: string;
  ClaimDate?: string;
  Project?: {
    Id?: string;
    Code?: string;
    Name?: string;
    OriginalId?: number;
  };
  Task?: {
    Id?: string;
    Code?: string;
    Name?: string;
  };
  Description?: string;
  Analysis?: string;
  VatCode?: string;
  VatRate?: number;
  ReceiptAttached?: boolean;
  Notes?: string;
  Justification?: string;
  HomeCurrencyValues?: {
    Quantity?: number;
    Rate?: number;
    Net?: number;
    Tax?: number;
    Gross?: number;
    TaxRate?: number;
    CurrencyIsoCode?: string;
  };
  EntryCurrencyValues?: {
    Quantity?: number;
    Rate?: number;
    Net?: number;
    Tax?: number;
    Gross?: number;
    TaxRate?: number;
    CurrencyIsoCode?: string;
  };
  ExchangeRate?: number;
  Workflow?: {
    Id?: string;
    Status?: string;
    StatusDescription?: string;
  };
  Net?: number;
  CurrencyIsoCode?: string;
  SystemGross?: number;
  Rate?: number;
  Quantity?: number;
  ValidationStatus?: string;
  CreatedTime?: string;
  UpdatedTime?: string;
}

export interface ExternalExpenseResponse {
  Items?: ExternalExpenseDetail[];
  TotalCount?: number;
}

/**
 * Map external expense API response to internal Expense format
 */
function mapExternalExpenseToInternal(
  externalExpense: ExternalExpenseDetail,
  projectId: number,
  index: number,
  sourceSystem: string = 'Access Expense'
): Expense {
  const amount = externalExpense.HomeCurrencyValues?.Gross 
    ?? externalExpense.Net 
    ?? externalExpense.SystemGross 
    ?? 0;
  
  const date = externalExpense.ClaimDate 
    ?? externalExpense.Header?.ExpenseDate 
    ?? externalExpense.CreatedTime 
    ?? new Date().toISOString();

  const category = externalExpense.Category 
    ?? externalExpense.ExpenseType?.Category 
    ?? externalExpense.ExpenseType?.Name 
    ?? 'General';

  const description = externalExpense.Description 
    ?? externalExpense.Header?.Description 
    ?? externalExpense.Notes 
    ?? '';

  return {
    id: index + 1,
    externalExpenseId: externalExpense.Id,
    projectId: projectId,
    employeeId: 0,
    date: date.split('T')[0],
    category: category,
    amount: amount,
    description: description,
    billable: true,
    reimbursable: true,
    approved: true,
    status: 'Approved',
    normalizedStatus: 'approved',
    receiptUrl: externalExpense.ReceiptAttached ? undefined : undefined,
    currency: externalExpense.HomeCurrencyValues?.CurrencyIsoCode ?? externalExpense.CurrencyIsoCode ?? 'GBP',
    exchangeRate: externalExpense.ExchangeRate,
    taxAmount: externalExpense.HomeCurrencyValues?.Tax,
    employeeName: [externalExpense.User?.FirstName, externalExpense.User?.LastName].filter(Boolean).join(' ') || undefined,
    externalUrl: externalExpense.User?.Url,
    merchantName: externalExpense.ExpenseType?.Name,
    sourceSystem: sourceSystem,
  };
}

/**
 * Fetch approved expenses from the external Expense API via the backend
 * Routes through: Frontend -> Backend -> Expense API
 * Falls back to mock data if the API request fails
 * 
 * @param projectId - The project ID to associate with the expenses
 * @param projectCode - The project code to filter expenses by (e.g., "AST-241")
 * @param subscriptionId - The expense subscription ID for the external API
 * @returns Array of Expense objects
 */
export async function fetchApprovedExpensesFromApi(
  projectId: number,
  projectCode: string | undefined,
  subscriptionId: string
): Promise<Expense[]> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockExpenses.filter((e: Expense) => e.projectId === projectId);
  }
  
  if (!subscriptionId) {
    console.warn('[Expense API] Missing subscriptionId — no expense integration configured');
    return [];
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/expenseintegration/approved-expenses?subscriptionId=${encodeURIComponent(subscriptionId)}`;
    
    const data = await apiFetch<ExternalExpenseResponse>(url);
    
    let items = parseExpenseResponse(data);
    
    // Filter expenses by project code if provided
    if (projectCode) {
      items = items.filter(item => item.Project?.Code === projectCode);
    }
    
    return items.map((item, index) => mapExternalExpenseToInternal(item, projectId, index));
  } catch (error) {
    console.error('[Expense API] Error fetching approved expenses:', error);
    return [];
  }
}

/**
 * Parse external expense API response into items array
 */
function parseExpenseResponse(data: unknown): ExternalExpenseDetail[] {
  let items: ExternalExpenseDetail[] = [];
  if (data && typeof data === 'object') {
    if (Array.isArray(data)) {
      items = data;
    } else if (Array.isArray((data as ExternalExpenseResponse).Items)) {
      items = (data as ExternalExpenseResponse).Items!;
    } else {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.items)) {
        items = dataObj.items as ExternalExpenseDetail[];
      } else if (Array.isArray(dataObj.data)) {
        items = dataObj.data as ExternalExpenseDetail[];
      } else if (Array.isArray(dataObj.value)) {
        items = dataObj.value as ExternalExpenseDetail[];
      } else if (Array.isArray(dataObj.$values)) {
        items = dataObj.$values as ExternalExpenseDetail[];
      }
    }
  }
  return items;
}

/**
 * Fetch approved credit card expenses from the external Expense API via the backend
 * Routes through: Frontend -> Backend -> Expense API
 * Falls back to empty array if the API request fails
 * 
 * @param projectId - The project ID to associate with the expenses
 * @param projectCode - The project code to filter expenses by (e.g., "AST-241")
 * @param subscriptionId - The expense subscription ID for the external API
 * @returns Array of Expense objects
 */
export async function fetchApprovedCreditCardExpensesFromApi(
  projectId: number,
  projectCode: string | undefined,
  subscriptionId: string
): Promise<Expense[]> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  }
  
  if (!subscriptionId) {
    console.warn('[Expense API] Missing subscriptionId for credit card expenses');
    return [];
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/expenseintegration/approved-credit-card-expenses?subscriptionId=${encodeURIComponent(subscriptionId)}`;
    
    const data = await apiFetch<ExternalExpenseResponse>(url);
    
    let items = parseExpenseResponse(data);
    
    if (projectCode) {
      items = items.filter(item => item.Project?.Code === projectCode);
    }
    
    return items.map((item, index) => mapExternalExpenseToInternal(item, projectId, index, 'Access Expense (Credit Card)'));
  } catch (error) {
    console.error('[Expense API] Error fetching approved credit card expenses:', error);
    return [];
  }
}

/**
 * Fetch approved credit card expenses using company credentials
 * 
 * @param projectId - The project ID to associate with the expenses
 * @param projectCode - The project code to filter expenses by
 * @param companyId - The company ID to fetch credentials from
 * @returns Array of Expense objects
 */
export async function fetchApprovedCreditCardExpenses(
  projectId: number,
  projectCode?: string,
  company?: { instanceId?: string; expenseSubscriptionId?: string } | null
): Promise<Expense[]> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  }
  
  if (!company?.expenseSubscriptionId) {
    console.warn('[Expense API] No expenseSubscriptionId provided for credit card expenses');
    return [];
  }
  
  return fetchApprovedCreditCardExpensesFromApi(
    projectId, projectCode, company.expenseSubscriptionId
  );
}

/**
 * Check if expense integration is configured for a company
 * Returns true if the company has all required expense credentials configured
 * 
 * @param company - The company to check
 * @returns boolean indicating if expense integration is available
 */
export function isExpenseIntegrationAvailable(company: Company | null | undefined): boolean {
  if (!company) return false;
  return !!company.expenseSubscriptionId;
}

/**
 * Fetch approved expenses using company credentials
 * Fetches the company data first to get the expense credentials, then fetches expenses
 * 
 * @param projectId - The project ID to associate with the expenses
 * @param projectCode - The project code to filter expenses by (e.g., "AST-241")
 * @param companyId - The company ID to fetch credentials from
 * @returns Array of Expense objects
 */
export async function fetchApprovedExpenses(
  projectId: number,
  projectCode?: string,
  company?: { instanceId?: string; expenseSubscriptionId?: string } | null
): Promise<Expense[]> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockExpenses.filter((e: Expense) => e.projectId === projectId);
  }
  
  if (!company?.expenseSubscriptionId) {
    console.warn('[Expense API] No expenseSubscriptionId provided');
    return [];
  }
  
  return fetchApprovedExpensesFromApi(
    projectId, projectCode, company.expenseSubscriptionId
  );
}
